/**
 * GuestProfile — faithful port of the canonical guest profile screen.
 * 2-col grid: left (profile card + tab card) | right 360px (booking responses, segments, connected systems)
 * Modals: send message · invite to event · edit profile · add to segment
 */
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useViewport } from "../../shell/useResponsive";
import { SectionRail, SectionHeaderBar, BackBox, type SectionRailGroup } from "../shared/section-rail";
import { Dropdown, SegmentedControl, alpha, Avatar, Btn, Card, Icon, IconBtn, Input, MoreMenu, Pill, Skeleton, Tab, T, tx } from "@v0y4ge/jrni-ui";
import BookAppointmentSheet from "../appointments/BookAppointmentSheet";
import { Screen } from "../../shell/chrome";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { createGuest, createGuestNote, listGuestNotes, updateGuest, deleteGuest, sendGuestDirectMessage, bulkAddGuestsToSegment, listGuestSegments, getGuestProfileBundle } from "@v0y4ge/api-client";
import type { GuestProfileBundleResponse } from "@v0y4ge/api-contracts";
import { useGuests, cachedGuest, type Guest } from "./useGuests";
import { useEvents } from "../events/useEvents";
import { logUpsert } from "../../lib/audit";
import { useT, t as i18nT } from "../../lib/i18n";
import { friendlyDate, friendlyDateTime } from "../../lib/datetime";

/** Shared shape for the editable profile summary (name/contact + VIP/press flags). */
interface ProfileState { name: string; email: string; phone?: string; company?: string; title?: string; vip?: boolean; press?: boolean; }

/* ── Helpers ──────────────────────────────────────────────────────── */
function ProfileStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ fontSize: 11.5, color: T.soft, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 14, color: T.n1, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, fontSize: 13 }}>
      <span style={{ color: T.soft, flexShrink: 0 }}>{k}</span>
      <span style={{ color: T.n1, fontWeight: 500, textAlign: "right" as const }}>{v}</span>
    </div>
  );
}
function SyncRow({ brand, name, id, synced, onClick }: { brand: string; name: string; id: string; synced: string; onClick?: () => void }) {
  const t = useT();
  return (
    <div onClick={onClick} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", cursor: onClick ? "pointer" : "default" }}>
      <span style={{ width: 28, height: 28, borderRadius: 6, background: brand, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 800, fontSize: 11, color: "#fff" }}>{name[0]}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{name}</div>
        <div style={{ fontSize: 11.5, color: T.soft }}>{id} {t("guests.profile.synced", "· synced")}{" "}{synced}</div>
      </div>
      <Icon.chev size={12} dir="right" style={{ color: T.soft }} />
    </div>
  );
}

/* ── Tab bodies ───────────────────────────────────────────────────── */
type BundleState = { status: "loading" } | { status: "error" } | { status: "ok"; data: GuestProfileBundleResponse };
/* Every tab reads the ONE profile bundle: rows-shaped skeleton while it loads, the refusal with Retry. */
function TabSkeleton({ mark }: { mark: "circle" | "rect" }) {
  const t = useT();
  return (
    <div role="status" aria-busy="true" aria-label={t("common.status.loading", "Loading…")}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ padding: "12px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center" }}>
          <Skeleton variant={mark} width={mark === "circle" ? 24 : 32} height={mark === "circle" ? 24 : 32} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}><Skeleton variant="text" width="55%" height={13} /><Skeleton variant="text" width="35%" height={11} /></div>
        </div>
      ))}
    </div>
  );
}
function TabError({ text, onRetry }: { text: string; onRetry: () => void }) {
  const t = useT();
  return (
    <div role="alert" style={{ padding: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: T.soft, fontSize: 13 }}>
      {text}
      <Btn variant="secondary" size="sm" onClick={onRetry}>{t("common.actions.retry", "Retry")}</Btn>
    </div>
  );
}
function TimelineTab({ bundle, onRetry }: { bundle: BundleState; onRetry: () => void }) {
  const t = useT();
  if (bundle.status === "loading") return <TabSkeleton mark="circle" />;
  if (bundle.status === "error") return <TabError text={t("guests.profile.activityLoadFailed", "Couldn't load activity")} onRetry={onRetry} />;
  const items = bundle.data.timeline;
  if (!items.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.noActivityYet", "No activity yet.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((it, i) => (
        <li key={it.id} style={{ padding: "12px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ width: 24, height: 24, borderRadius: 999, background: alpha(T.p1, "18"), color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}><span style={{ width: 8, height: 8, borderRadius: 999, background: T.p1 }} /></span>
          <div><div style={{ fontSize: 13, color: T.n1 }}>{it.title}</div>{it.body ? <div style={{ fontSize: 12, color: T.n2, marginTop: 2 }}>{it.body}</div> : null}<div style={{ fontSize: 11.5, color: T.soft, marginTop: 2 }}>{friendlyDateTime(it.timestamp)}</div></div>
        </li>
      ))}
    </ul>
  );
}
function EventsTab({ nav, bundle, onRetry }: { nav: ReturnType<typeof useNav>; bundle: BundleState; onRetry: () => void }) {
  const t = useT();
  if (bundle.status === "loading") return <TabSkeleton mark="rect" />;
  if (bundle.status === "error") return <TabError text={t("guests.profile.eventHistoryLoadFailed", "Couldn't load event history")} onRetry={onRetry} />;
  const events = bundle.data.events.map((e) => ({
    id: e.eventId, title: e.title, date: friendlyDate(e.date), role: e.role ?? e.guestStatus,
    tone: (e.guestStatus === "checked-in" ? "success" : e.guestStatus === "cancelled" || e.guestStatus === "declined" || e.guestStatus === "no-show" ? "warn" : "neutral") as "success" | "neutral" | "warn",
  }));
  if (!events.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.noEventsYet", "This guest hasn't registered for any events yet.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {events.map((e, i) => (
        <li key={e.id} onClick={() => nav.go("event.detail", { id: e.id })} style={{ padding: "14px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center", cursor: "pointer" }}>
          <span style={{ width: 36, height: 36, borderRadius: 6, background: T.p1, color: "#fff", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{e.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
          <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{e.title}</div><div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{e.date}</div></div>
          <Pill tone={e.tone}>{e.role}</Pill>
        </li>
      ))}
    </ul>
  );
}
function CommsTab({ bundle, onRetry }: { bundle: BundleState; onRetry: () => void }) {
  const t = useT();
  if (bundle.status === "loading") return <TabSkeleton mark="rect" />;
  if (bundle.status === "error") return <TabError text={t("guests.profile.commsLoadFailed", "Couldn't load messages")} onRetry={onRetry} />;
  const rows = bundle.data.comms.map((c) => ({
    id: c.id, ch: c.channel, subject: c.subject, when: friendlyDateTime(c.timestamp), status: c.status,
    tone: (c.status === "failed" ? "warn" : c.status === "opened" || c.status === "clicked" ? "success" : "info") as "success" | "info" | "warn",
  }));
  if (!rows.length) return <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("guests.profile.noCommsYet", "No messages sent to this guest yet.")}</div>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {rows.map((r, i) => (
        <li key={r.id} style={{ padding: "12px 20px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 28, height: 28, borderRadius: 6, background: r.ch === "sms" ? alpha(T.green1, "18") : T.p4, color: r.ch === "sms" ? T.green1 : T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {r.ch === "sms" ? <Icon.msg size={14} /> : <Icon.mail size={14} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{r.subject}</div>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{r.when}</div>
          </div>
          <Pill tone={r.tone}>{r.status}</Pill>
        </li>
      ))}
    </ul>
  );
}
function NotesTab({ notes, draft, setDraft, addNote }: { notes: { id: string; who: string; when: string; body: string }[]; draft: string; setDraft: (v: string) => void; addNote: () => void }) {
  const t = useT();
  return (
    <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      {can("guests.write") ? (
        <div style={{ display: "flex", gap: 8 }}>
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("guests.profile.addANote", "Add a note…")} onKeyDown={(e) => e.key === "Enter" && addNote()} style={{ flex: 1 }} />
          <Btn onClick={addNote} disabled={!draft.trim()}>{t("common.actions.add", "Add")}</Btn>
        </div>
      ) : null}
      {notes.map((n) => (
        <div key={n.id} style={{ padding: "12px 14px", background: T.n6, borderRadius: 8 }}>
          <div style={{ fontSize: 13.5, color: T.n1, lineHeight: 1.5 }}>{n.body}</div>
          <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>{n.who} · {n.when}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Modals ─────────────────────────────────────────────────────── */
function ModalWrap({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div style={{ background: T.white, borderRadius: 14, padding: 28, width: 480, maxHeight: "85vh", overflow: "auto", boxShadow: T.shadowLg }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.n1 }}>{title}</div>
          <IconBtn size={32} onClick={onClose}><Icon.x size={18} /></IconBtn>
        </div>
        {children}
      </div>
    </div>
  );
}
function errText(e: unknown): string {
  const st = (e as { status?: number })?.status; const m = (e instanceof Error && e.message.trim()) || "";
  return m || (st ? i18nT("guests.profile.errStatus.icu", "The server answered {value0}.", { value0: st }) : i18nT("guests.profile.errGeneric", "Please try again."));
}

/* Real send (2026-10-05): sendGuestDirectMessage (POST /v1/admin/guests/:id/messages). It used
   to toast "Message sent" without sending anything. */
function SendMsgModal({ guest, guestId, onClose }: { guest: { name: string; email: string; phone?: string }; guestId: string; onClose: () => void }) {
  const t = useT(); // i18n pilot — common.actions.* keys
  const [ch, setCh] = useState("email");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    try {
      await sendGuestDirectMessage(guestId, { channel: ch as "email" | "sms", body: body.trim() });
      toast({ tone: "success", title: t("guests.profile.messageSent", "Message sent"), subtitle: ch === "sms" ? guest.phone : guest.email });
      onClose();
    } catch (e) {
      toast({ tone: "danger", title: t("guests.profile.messageFailed", "Message not sent"), subtitle: errText(e) });
    } finally { setSending(false); }
  };
  return (
    <ModalWrap title={i18nT("guests.profile.messageGuestName.icu", "Message {value0}", { value0: guest.name })} onClose={onClose}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <SegmentedControl<string> full ariaLabel={t("guests.profile.ariaLabel", "Message channel")} value={ch} onChange={setCh} options={[{ value: "email", label: t("guests.profile.label", "Email") }, { value: "sms", label: "SMS" }]} />
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("guests.profile.writeYourMessage", "Write your message…")} rows={5} style={{ width: "100%", resize: "vertical", border: `1px solid ${T.n4}`, borderRadius: 8, padding: "10px 12px", fontFamily: T.font, fontSize: 13, color: T.n1, outline: "none", boxSizing: "border-box" }} />
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn icon={<Icon.send size={14} />} disabled={!body.trim() || sending || (ch === "sms" && !guest.phone)} onClick={() => void send()}>{t("common.actions.send", "Send")}</Btn>
      </div>
    </ModalWrap>
  );
}
function EditProfileModal({ profile, onSave, onClose }: { profile: ProfileState; onSave: (p: ProfileState) => Promise<boolean>; onClose: () => void }) {
  const t = useT(); // i18n pilot — common.actions.* keys
  const [form, setForm] = useState({ ...profile });
  const [saving, setSaving] = useState(false);
  const f = { display: "flex", flexDirection: "column" as const, gap: 5 };
  const lbl = { fontSize: 12.5, fontWeight: 600, color: T.n2 };
  return (
    <ModalWrap title={t("guests.profile.editProfile", "Edit profile")} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={f}><span style={lbl}>{t("guests.profile.firstName", "First name")}</span><Input value={form.name.split(" ")[0]} onChange={(e) => setForm((x) => ({ ...x, name: e.target.value + " " + x.name.split(" ").slice(1).join(" ") }))} /></div>
          <div style={f}><span style={lbl}>{t("guests.profile.lastName", "Last name")}</span><Input value={form.name.split(" ").slice(1).join(" ")} onChange={(e) => setForm((x) => ({ ...x, name: x.name.split(" ")[0] + " " + e.target.value }))} /></div>
        </div>
        <div style={f}><span style={lbl}>{t("guests.profile.email", "Email")}</span><Input value={form.email} onChange={(e) => setForm((x) => ({ ...x, email: e.target.value }))} type="email" /></div>
        <div style={f}><span style={lbl}>{t("guests.profile.phone", "Phone")}</span><Input value={form.phone ?? ""} onChange={(e) => setForm((x) => ({ ...x, phone: e.target.value }))} /></div>
        <div style={f}><span style={lbl}>{t("guests.profile.company", "Company")}</span><Input value={form.company ?? ""} onChange={(e) => setForm((x) => ({ ...x, company: e.target.value }))} /></div>
        {/* No Title field: GuestUpdateInput has no title, so an edit here could never be saved (2026-10-05). */}
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn disabled={saving} onClick={async () => { setSaving(true); const ok = await onSave(form); setSaving(false); if (ok) onClose(); }}>{saving ? t("guests.new.saving", "Saving…") : t("common.actions.save", "Save")}</Btn>
      </div>
    </ModalWrap>
  );
}
/* Real membership (2026-10-05): bulkAddGuestsToSegment (POST /v1/admin/guests/bulk/segments) with
   the tenant's own segments (listGuestSegments) — or a new name, which the op accepts. The list
   used to be six hardcoded names and the add only changed local state. */
function AddSegmentModal({ guestId, current, onAdded, onClose }: { guestId: string; current: string[]; onAdded: (s: string) => void; onClose: () => void }) {
  const t = useT();
  const [all, setAll] = useState<{ id: string; name: string }[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { let live = true; listGuestSegments().then((r: any) => { if (live) setAll(((r.segments ?? r.data ?? []) as any[]).map((s) => ({ id: String(s.id), name: String(s.name ?? "") })).filter((s) => s.name)); }).catch(() => { if (live) setAll([]); }); return () => { live = false; }; }, []);
  const add = async (seg: { id?: string; name: string }) => {
    if (busy) return; setBusy(true);
    try {
      await bulkAddGuestsToSegment({ guestIds: [guestId], ...(seg.id ? { segmentId: seg.id } : { segmentName: seg.name }) } as any);
      onAdded(seg.name);
      toast({ tone: "success", title: i18nT("guests.profile.addedToS.icu", "Added to {value0}", { value0: seg.name }) });
      onClose();
    } catch (e) {
      toast({ tone: "danger", title: t("guests.profile.segmentAddFailed", "Couldn't add to the segment"), subtitle: errText(e) });
    } finally { setBusy(false); }
  };
  const available = (all ?? []).filter((s) => !current.includes(s.name));
  return (
    <ModalWrap title={t("guests.profile.addToSegment", "Add to segment")} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {all === null ? <p style={{ color: T.soft, fontSize: 13 }}>{t("guests.profile.loadingSegments", "Loading segments…")}</p> : null}
        {available.map((s) => (
          /* ui-raw: full-width clickable list/picker row — no row-as-button component exists */
          <button key={s.id} disabled={busy} onClick={() => void add(s)}
            style={{ padding: "10px 14px", border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, fontSize: 13, fontWeight: 600, color: T.n1, cursor: "pointer", textAlign: "left" as const, fontFamily: T.font }}>
            {s.name}
          </button>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("guests.profile.newSegmentName", "Or a new segment name")} />
          <Btn variant="secondary" disabled={!name.trim() || busy} onClick={() => void add({ name: name.trim() })}>{t("common.actions.add", "Add")}</Btn>
        </div>
      </div>
    </ModalWrap>
  );
}

/* ═══ Add guest (2026-10-05) ═════════════════════════════════════════
   "Add guest" routes here with ?new=1. It used to fall through to the profile with no id,
   whose fallback is the first guest — so it opened Marcus Reilly's record. Now it is a real
   create form on the createGuest op (POST /v1/admin/guests); on success it opens the new
   guest's profile. A separate component, so the new/view switch never changes hook order. */
function NewGuestForm() {
  const t = useT();
  const nav = useNav();
  const { events } = useEvents();
  const presetEvent = typeof nav.params?.eventId === "string" ? nav.params.eventId : "";
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", organization: "", eventId: presetEvent, status: "registered", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((x) => ({ ...x, [k]: e.target.value }));
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  /* A guest is a registration: the server refuses a create with no existing event, so an event is required. */
  const canSave = !!(form.firstName.trim() || form.lastName.trim()) && emailOk && !!form.eventId && !saving;
  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr(null);
    try {
      const body: Record<string, unknown> = { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim(), status: form.status, source: "manual" };
      if (form.phone.trim()) body.phone = form.phone.trim();
      if (form.organization.trim()) body.organization = form.organization.trim();
      body.eventId = form.eventId;
      if (form.notes.trim()) body.notes = form.notes.trim();
      const res: any = await createGuest(body as any);
      const g = res?.guest ?? res;
      toast({ tone: "success", title: t("guests.new.created", "Guest added"), subtitle: [form.firstName, form.lastName].filter(Boolean).join(" ") });
      if (g?.id) nav.go("guests.profile", { id: g.id }); else nav.go("guests");
    } catch (e) {
      /* Never a silent failure: a blank API message falls back to plain words + the status. */
      const status = (e as { status?: number })?.status;
      const msg = (e instanceof Error && e.message.trim()) || "";
      setErr(msg || (status ? t("guests.new.failedStatus", "Couldn't add the guest ({status}) — try again.", { status }) : t("guests.new.failed", "The guest wasn't saved — try again.")));
    } finally { setSaving(false); }
  };
  const fld = { display: "flex", flexDirection: "column" as const, gap: 5, minWidth: 0 };
  const lbl = { fontSize: 12.5, fontWeight: 600, color: T.n2 };
  const sel = { height: 36, borderRadius: 8, border: `1px solid ${T.border}`, padding: "0 10px", fontFamily: T.font, fontSize: 13.5, background: T.white, color: T.n1 } as const;
  return (
    <Screen rail="guests">
      <SectionHeaderBar
        lead={<BackBox label={t("guests.profile.guests", "Guests")} onClick={() => nav.go("guests")} />}
        title={t("guests.list.addGuest", "Add guest")}
        sub={t("guests.new.subRequiresEvent", "Register a guest for an event.")}
        actions={<>
          <Btn variant="secondary" onClick={() => nav.go("guests")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn disabled={!canSave} onClick={() => void save()}>{saving ? t("guests.new.saving", "Saving…") : t("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "28px 32px", background: T.canvas }}>
        <Card style={{ padding: 24, maxWidth: 720, display: "flex", flexDirection: "column", gap: 14 }}>
          {!presetEvent && !(events as any[]).some((ev) => ev.status !== "past") ? <div role="status" style={{ fontSize: 13, color: T.n2 }}>{t("guests.new.noEventsYet", "There are no upcoming events to register a guest for yet. Create an event first.")}</div> : null}
          {err ? <div role="alert" style={{ fontSize: 13, color: T.red1, background: T.red2, borderRadius: 8, padding: "10px 12px" }}>{err}</div> : null}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label style={fld}><span style={lbl}>{t("guests.profile.firstName", "First name")}</span><Input value={form.firstName} onChange={set("firstName")} autoFocus /></label>
            <label style={fld}><span style={lbl}>{t("guests.profile.lastName", "Last name")}</span><Input value={form.lastName} onChange={set("lastName")} /></label>
          </div>
          <label style={fld}><span style={lbl}>{t("guests.profile.email", "Email")} *</span><Input type="email" value={form.email} onChange={set("email")} />
            {form.email && !emailOk ? <span style={{ fontSize: 12, color: T.red1 }}>{t("guests.new.emailInvalid", "Enter a valid email address.")}</span> : null}</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label style={fld}><span style={lbl}>{t("guests.profile.phone", "Phone")}</span><Input value={form.phone} onChange={set("phone")} /></label>
            <label style={fld}><span style={lbl}>{t("guests.profile.company", "Company")}</span><Input value={form.organization} onChange={set("organization")} /></label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <label style={fld}><span style={lbl}>{t("guests.new.event", "Event")}</span>
              <Dropdown<string> portal aria-label={t("guests.new.event", "Event")} value={form.eventId} placeholder={t("guests.new.chooseEvent", "Choose an event")} onChange={(v) => setForm((x) => ({ ...x, eventId: v }))} options={[...(events as any[]).filter((ev) => ev.status !== "past").map((ev) => ({ value: String(ev.id), label: `${ev.title}${ev.date ? " · " + ev.date : ""}` }))]} /></label>
            <label style={fld}><span style={lbl}>{t("guests.list.status", "Status")}</span>
              <Dropdown<string> portal aria-label={t("guests.list.status", "Status")} value={form.status} onChange={(v) => setForm((x) => ({ ...x, status: v }))} options={[{ value: "registered", label: t("guests.list.registered", "Registered") }, { value: "waitlist", label: t("guests.list.waitlist", "Waitlist") }, { value: "checked-in", label: t("guests.list.checkedIn", "Checked in") }]} /></label>
          </div>
          <label style={fld}><span style={lbl}>{t("guests.profile.notes", "Notes")}</span>
            <textarea value={form.notes} onChange={set("notes")} rows={3} style={{ ...sel, height: "auto", padding: "8px 10px", resize: "vertical" }} /></label>
        </Card>
      </div>
    </Screen>
  );
}

export default function GuestProfile() {
  const nav = useNav();
  return nav.params?.new ? <NewGuestForm key="new" /> : <GuestProfileView key={String(nav.params?.id ?? "")} />;
}

/* ═══ Main screen ════════════════════════════════════════════════════ */
function GuestProfileView() {
  const t = useT();
  const nav = useNav();
  const { guests, loading: guestsLoading, reload: reloadGuests } = useGuests();
  const { events: allEvents } = useEvents();

  /* The "Guests & Customers" tab-total read went with the sub-tabs (2026-10-05): nothing shows it now. */
  const paramId = (nav.params?.id as string) || "";
  /* Paint from the row the profile was opened from (cache) while the read revalidates. */
  const g = useMemo(() => guests.find((x) => x.id === paramId) || cachedGuest(paramId) || undefined, [paramId, guests, guestsLoading]);

  const [tab, setTab] = useState("timeline");
  const [modal, setModal] = useState<"send" | "invite" | "edit" | "addSegment" | null>(null);
  // APT-12 (2026-07-26): event→appointment pipeline — book a 1:1 follow-up
  // from the guest record via the ONE shared booking sheet (canonical family).
  const [bookFollowUp, setBookFollowUp] = useState(false);
  useEffect(() => { if (nav.params?.edit) setModal("edit"); }, [nav.params?.id]);

  const [segments, setSegments] = useState<string[]>(() => g?.segments?.slice() ?? []);
  useEffect(() => { setSegments(g?.segments?.slice() ?? []); }, [g?.id]);

  const [profile, setProfile] = useState<ProfileState>(() => ({
    name: g?.name ?? "", email: g?.email ?? "", phone: g?.phone, company: g?.company,
    title: (g as { title?: string } | undefined)?.title, // §3c (2026-07-19): from the record — never a baked "Managing Director"
    vip: g?.ticket === "VIP" || (g?.segments ?? []).includes("VIP") || (g?.tags ?? []).includes("VIP"),
    press: (g?.segments ?? []).includes("Press list") || g?.ticket === "Press",
  }));
  useEffect(() => {
    if (!g) return;
    setProfile({ name: g.name, email: g.email, phone: g.phone, company: g.company, title: (g as { title?: string }).title, vip: g.ticket === "VIP" || (g.segments ?? []).includes("VIP") || (g.tags ?? []).includes("VIP"), press: (g.segments ?? []).includes("Press list") || g.ticket === "Press" });
  }, [g?.id]);

  // §3c (2026-07-19): notes hydrate from + persist through the real guest-notes
  // ops — the old seeded "Imani Okafor" note fabricated guest correspondence.
  type NoteRow = { id: string; who: string; when: string; body: string };
  const asNoteRow = (n: { id: string; author: string; authorName?: string; when: string; body: string }): NoteRow => ({ id: n.id, who: n.authorName || n.author, when: friendlyDateTime(n.when), body: n.body });
  const [notes, setNotes] = useState<NoteRow[]>([]);
  useEffect(() => {
    let live = true;
    if (!g?.id) return;
    setNotes([]);
    listGuestNotes(g.id)
      .then((r) => { if (live) setNotes(r.notes.map(asNoteRow)); })
      .catch(() => { /* keep empty — never fabricate */ });
    return () => { live = false; };
  }, [g?.id]);
  /* Profile stats + timeline come from the server's profile bundle, read once per guest. */
  const [bundle, setBundle] = useState<BundleState>({ status: "loading" });
  const [bundleTry, setBundleTry] = useState(0);
  const retryBundle = () => setBundleTry((n) => n + 1);
  useEffect(() => {
    let live = true;
    if (!g?.id) return;
    setBundle({ status: "loading" });
    getGuestProfileBundle(g.id)
      .then((data) => { if (live) setBundle({ status: "ok", data }); })
      .catch(() => { if (live) setBundle({ status: "error" }); });
    return () => { live = false; };
  }, [g?.id, bundleTry]);
  const [noteDraft, setNoteDraft] = useState("");
  const addNote = async () => {
    if (!noteDraft.trim() || !g?.id) return;
    try {
      const r = await createGuestNote(g.id, { body: noteDraft.trim() });
      const created = ((r as { note?: unknown }).note ?? r) as { id: string; author: string; authorName?: string; when: string; body: string };
      setNotes((n) => [asNoteRow(created), ...n]);
      setNoteDraft("");
      toast({ tone: "success", title: t("guests.profile.noteAdded", "Note added") });
    } catch {
      toast({ tone: "warn", title: t("guests.profile.couldnTAddNote", "Couldn't add note"), subtitle: t("guests.profile.theNoteWasNotSavedTryAgain", "The note was not saved — try again.") });
    }
  };

  const close = () => setModal(null);

  /* Every action is a real operation now (2026-10-05) — they were toasts. VIP is the record's
     "VIP" tag (updateGuest.tags); there is no unsubscribe field on the update input, so that
     action is gone rather than pretending. */
  const toggleVip = async () => {
    if (!g) return;
    const next = !profile.vip;
    const tags = Array.from(new Set([...(((g as any).tags ?? []) as string[]).filter((x) => x !== "VIP"), ...(next ? ["VIP"] : [])]));
    try { await updateGuest(g.id, { tags } as any); setProfile((p) => ({ ...p, vip: next })); reloadGuests(); toast({ tone: next ? "success" : "neutral", title: next ? t("guests.profile.markedAsVip", "Marked as VIP") : t("guests.profile.vipStatusRemoved", "VIP status removed") }); }
    catch (e) { toast({ tone: "danger", title: t("guests.profile.updateFailed", "Couldn't update the guest"), subtitle: errText(e) }); }
  };
  const copyEmail = async () => {
    try { await navigator.clipboard.writeText(profile.email); toast({ tone: "info", title: t("guests.profile.emailCopied", "Email copied") }); }
    catch { toast({ tone: "warn", title: t("guests.profile.copyFailed", "Couldn't copy — your browser blocked the clipboard") }); }
  };
  const removeGuest = async () => {
    if (!g) return;
    if (!window.confirm(i18nT("guests.profile.confirmDelete.icu", "Delete {value0}? This removes the guest record.", { value0: profile.name }))) return;
    try { await deleteGuest(g.id); toast({ tone: "warn", title: t("guests.profile.guestDeleted", "Guest deleted"), subtitle: profile.name }); nav.go("guests"); }
    catch (e) { toast({ tone: "danger", title: t("guests.profile.deleteFailed", "Couldn't delete the guest"), subtitle: errText(e) }); }
  };
  const moreItems = [
    ...(can("guests.write") ? [{ icon: <Icon.star size={14} />, label: profile.vip ? t("guests.profile.removeVipStatus", "Remove VIP status") : t("guests.profile.markAsVip", "Mark as VIP"), onClick: () => void toggleVip() }] : []),
    { icon: <Icon.copy size={14} />, label: t("guests.profile.copyEmail", "Copy email"), onClick: () => void copyEmail() },
    ...(can("guests.write") ? ["divider" as const, { icon: <Icon.trash size={14} />, label: t("guests.profile.deleteGuest", "Delete guest"), danger: true, onClick: () => void removeGuest() }] : []),
  ];
  /* The bundle's booking responses carry the question's label, never its id. */
  const answerRows: [string, string][] = (bundle.status === "ok" ? bundle.data.bookingResponses : [])
    .map((r) => [r.question, Array.isArray(r.value) ? r.value.join(", ") : String(r.value ?? "")] as [string, string]).filter(([, v]) => v !== "");
  const extId = String((g as any)?.externalId ?? "");
  const personStats = (() => {
    if (bundle.status === "loading") { const sk = <Skeleton variant="text" width={48} height={14} />; return { attended: sk, ltv: sk, lastSeen: sk }; }
    if (bundle.status === "error") return { attended: "—", ltv: "—", lastSeen: "—" };
    const st = bundle.data.stats;
    return { attended: String(st.eventsAttended), ltv: new Intl.NumberFormat(undefined, { style: "currency", currency: st.currency }).format(st.lifetimeValueMinor / 100), lastSeen: st.lastSeenAt ? new Date(st.lastSeenAt).toLocaleDateString() : "—" };
  })();

  /* Sections — ONE list shared with the directory's customer detail (2026-10-05), in the same
     order; each profile shows the sections it has data for. */
  const desktop = useViewport() === "desktop";
  const [section, setSection] = useState<"overview" | "timeline" | "events" | "comms" | "notes" | "responses" | "segments" | "systems">("overview");
  const SECTION_LABELS: Record<typeof section, string> = {
    overview: t("guests.profile.section.overview", "Overview"), timeline: t("guests.profile.timeline", "Timeline"),
    events: t("guests.profile.events", "Events"), comms: t("guests.profile.comms", "Comms"), notes: t("guests.profile.notes", "Notes"),
    responses: t("guests.profile.bookingResponses", "Booking responses"), segments: t("guests.profile.segments", "Segments"),
    systems: t("guests.profile.connectedSystems", "Connected systems"),
  };
  const row = (id: typeof section, count?: number) => ({ id, label: SECTION_LABELS[id], count, active: section === id, onClick: () => setSection(id) });
  const sectionGroups: SectionRailGroup[] = [
    { id: "profile", heading: t("guests.profile.section.profile", "Profile"), role: "tablist", rows: [row("overview")] },
    { id: "activity", heading: t("guests.profile.section.activity", "Activity"), role: "tablist", rows: [row("timeline"), row("events"), row("comms"), row("notes", notes.length)] },
    { id: "details", heading: t("guests.profile.section.details", "Details"), role: "tablist", rows: [row("responses"), row("segments", segments.length), row("systems")] },
  ];
  const sectionLabel = SECTION_LABELS[section];

  /* Never a blank page while the record loads: the shell, header and rail frame stay put. */
  if (!g) return (
    <Screen rail="guests">
      <SectionHeaderBar lead={<BackBox label={t("guests.profile.guests", "Guests")} onClick={() => nav.go("guests")} />} title={guestsLoading ? t("guests.profile.loading", "Loading profile…") : t("guests.profile.notFound", "Guest not found")} />
      {!guestsLoading ? (
        <div style={{ padding: "28px 32px" }}>
          <Card style={{ padding: 24, maxWidth: 560, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 13.5, color: T.n2 }}>{t("guests.profile.notFoundBody", "This guest may have been removed, or the link is incomplete.")}</span>
            <Btn variant="secondary" size="sm" onClick={() => nav.go("guests")}>{t("guests.profile.backToGuests", "Back to guests")}</Btn>
          </Card>
        </div>
      ) : null}
    </Screen>
  );

  const init = g.name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

  return (
    /* Events section is the primary nav (Attendees active) since the guests
       rail item retired with the 2026-09-10 customers drop; this screen's own
       three tabs are the secondary row. */
    /* Customers for the event roles (2026-10-05): lights the "guests" rail item (it declared
       Events). Layout follows Edit Event: header bar, a left rail of sections, one section at a
       time beside it — two columns. The old right sidebar's cards are sections now. */
    <Screen rail="guests">
      <SectionHeaderBar
        lead={<BackBox label={t("guests.profile.guests", "Guests")} onClick={() => nav.go("guests")} />}
        title={profile.name}
        sub={[profile.email, profile.company].filter(Boolean).join(" · ")}
        actions={<>
          <Btn icon={<Icon.send size={14} />} onClick={() => setModal("send")}>{t("guests.profile.sendMessage", "Send message")}</Btn>
          <Btn variant="secondary" icon={<Icon.calendar size={14} />} onClick={() => setModal("invite")}>{t("guests.profile.addToEvent", "Add to event")}</Btn>
          {can("appointments.book") ? <Btn variant="secondary" icon={<Icon.clock size={14} />} onClick={() => setBookFollowUp(true)} testid="guest-book-followup">{t("guests.profile.bookFollowUp", "Book a follow-up")}</Btn> : null}
          <Btn variant="ghost" icon={<Icon.edit size={14} />} onClick={() => setModal("edit")}>{t("guests.profile.editProfile", "Edit profile")}</Btn>
          <MoreMenu label={t("guests.profile.moreActions", "More actions")} size={36} iconSize={16} width={240} header={t("guests.profile.guestActions", "Guest actions")} items={moreItems} />
        </>}
      />
      <div style={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {desktop ? <SectionRail ariaLabel={t("guests.profile.sections", "Profile sections")} groups={sectionGroups} /> : null}
        <div style={{ flex: 1, minWidth: 0, overflow: "auto", padding: desktop ? "28px 32px" : "16px", background: T.canvas }}>
          {!desktop ? (
            <div role="tablist" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 14 }}>
              {sectionGroups.flatMap((g) => g.rows).map((r) => (
                <button key={r.id} type="button" role="tab" aria-selected={!!r.active} onClick={r.onClick} style={{ flex: "none", padding: "6px 12px", borderRadius: 999, border: `1px solid ${r.active ? T.p1 : T.border}`, background: r.active ? T.p4 : T.white, color: r.active ? T.p1 : T.n1, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{r.label}</button>
              ))}
            </div>
          ) : null}
          <div style={{ maxWidth: 880, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{sectionLabel}</div>
            {section === "overview" ? (
              <Card style={{ padding: 24, display: "flex", gap: 20, alignItems: "flex-start" }}>
            <Avatar size={72} initials={init} color={g.color ?? T.p1} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 16, fontWeight: 700, color: T.n1, margin: 0 }}>{profile.name}</h1>
                {profile.vip  ? <Pill tone="primary"><Icon.star size={10} /> {t("guests.profile.vip", "VIP")}</Pill> : null}
                {profile.press ? <Pill tone="warn">{t("guests.profile.pressList", "Press list")}</Pill> : null}
                {g.status === "checked-in" ? <Pill tone="success" dot>{t("guests.profile.checkedIn", "Checked in")}</Pill> :
                 g.status === "waitlist"   ? <Pill tone="warn">{t("guests.profile.waitlist", "Waitlist")}</Pill> :
                 g.status === "no-show"    ? <Pill tone="danger">{t("guests.profile.noShow", "No-show")}</Pill> :
                                             <Pill tone="info">{t("guests.profile.registered", "Registered")}</Pill>}
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8, color: T.n2, fontSize: 13, flexWrap: "wrap" }}>
                <a href={`mailto:${profile.email}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.n2, textDecoration: "none" }}>
                  <Icon.mail size={13} /> {profile.email}
                </a>
                {profile.phone ? (
                  <a href={`tel:${profile.phone.replace(/[^+\d]/g, "")}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.n2, textDecoration: "none" }}>
                    <Icon.phone size={13} /> {profile.phone}
                  </a>
                ) : null}
                {profile.company ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.tags size={13} /> {profile.company}</span> : null}
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 16, flexWrap: "wrap" }}>
                {/* §3c (2026-07-19): no invented metrics — "—" until real profile/
                    activity ops exist (declared as guest-profile stats ask). */}
                {/* Counted from the guest rows linked to the same customer record (customerId), else this registration alone. */}
                <ProfileStat label={t("guests.profile.checkIns", "Check-ins")} value={personStats.attended} />
                {can("billing.read") ? <ProfileStat label={t("guests.profile.lifetimeValue", "Lifetime value")} value={personStats.ltv} /> : null}
                <ProfileStat label={t("guests.profile.lastSeen", "Last seen")} value={personStats.lastSeen} />
                <ProfileStat label={t("guests.profile.source", "Source")} value={g.source || "—"} />
              </div>
            </div>
              </Card>
            ) : section === "timeline" || section === "events" || section === "comms" || section === "notes" ? (
              <Card>
                {section === "timeline" && <TimelineTab bundle={bundle} onRetry={retryBundle} />}
                {section === "events"   && <EventsTab nav={nav} bundle={bundle} onRetry={retryBundle} />}
                {section === "comms"    && <CommsTab bundle={bundle} onRetry={retryBundle} />}
                {section === "notes"    && <NotesTab notes={notes} draft={noteDraft} setDraft={setNoteDraft} addNote={addNote} />}
              </Card>
            ) : section === "responses" ? (
              <Card style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={tx.label}>{t("guests.profile.bookingResponses", "Booking responses")}</div>
              <Btn variant="ghost" size="sm" icon={<Icon.edit size={12} />} onClick={() => nav.go("guests.responses", { id: g.id })}>{t("common.actions.edit", "Edit")}</Btn>
            </div>
            {/* The guest's answers from the profile bundle, labelled by question. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {bundle.status === "loading" ? (
                <div role="status" aria-busy="true" aria-label={t("common.status.loading", "Loading…")} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[0, 1, 2].map((i) => <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12 }}><Skeleton variant="text" width="35%" height={13} /><Skeleton variant="text" width="25%" height={13} /></div>)}
                </div>
              ) : bundle.status === "error" ? (
                <TabError text={t("guests.profile.responsesLoadFailed", "Couldn't load booking responses")} onRetry={retryBundle} />
              ) : answerRows.length === 0 ? (
                <div style={{ fontSize: 12.5, color: T.soft }}>{t("guests.profile.noResponses", "No booking responses recorded for this guest.")}</div>
              ) : answerRows.map(([k, v], i) => <KV key={i} k={k} v={v} />)}
            </div>
              </Card>
            ) : section === "segments" ? (
              <Card style={{ padding: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {segments.length === 0 ? (
                <div style={{ fontSize: 12, color: T.soft, padding: "6px 2px" }}>{t("guests.profile.notInAnySegmentYet", "Not in any segment yet.")}</div>
              ) : segments.map((s) => (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: s === "VIP" ? T.p1 : s === "Press list" ? "#C13584" : T.blue1 }} />
                  <span style={{ flex: 1, fontSize: 13, color: T.n1, fontWeight: 500, cursor: "pointer" }} onClick={() => nav.go("segments")}>{s}</span>
                  {/* No remove (×): there is no single-guest segment-removal operation; it only changed local state. */}
                </div>
              ))}
            </div>
            <Btn variant="ghost" size="sm" icon={<Icon.plus size={12} />} onClick={() => setModal("addSegment")} style={{ marginTop: 10 }}>{t("guests.profile.addToSegment", "Add to segment")}</Btn>
              </Card>
            ) : (
              <Card style={{ padding: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {/* The record's real link (source + externalId); the two sample CRM rows are gone. */}
              {extId ? (
                <SyncRow brand={g.source === "Salesforce" || /salesforce/i.test(String(g.source)) ? "#00A1E0" : T.p1} name={String(g.source || t("guests.profile.externalSystem", "External system"))} id={extId} synced={String((g as any).externalObjectType ?? "")} onClick={() => nav.go("settings.integ")} />
              ) : (
                <div style={{ fontSize: 12.5, color: T.soft }}>{i18nT("guests.profile.notLinked.icu", "Not linked to an external system. Source: {value0}.", { value0: g.source || "—" })}</div>
              )}
            </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal === "send"       ? <SendMsgModal guest={profile} guestId={g.id} onClose={close} /> : null}
      {modal === "invite"     ? (
        <ModalWrap title={t("guests.profile.addToEvent", "Add to event")} onClose={close}>
          {/* A real registration (createGuest with this person + the event) — it toasted "Invite sent"
              and sent nothing. Per-person invites have no op (InviteCreateInput is segment-wide). */}
          <p style={{ fontSize: 13, color: T.soft }}>{i18nT("guests.profile.addToEventBody.icu", "Register {value0} for an upcoming event.", { value0: profile.name })}</p>
          {(allEvents as any[]).filter((ev) => ev.status === "upcoming" || ev.status === "live").slice(0, 8).map((ev) => (
            /* ui-raw: full-width clickable list/picker row — no row-as-button component exists */
            <button key={ev.id} onClick={async () => {
              try {
                const [fn, ...rest] = profile.name.split(" ");
                await createGuest({ firstName: fn, lastName: rest.join(" "), email: profile.email, ...(profile.phone ? { phone: profile.phone } : {}), ...(profile.company ? { organization: profile.company } : {}), eventId: ev.id, status: "registered", source: "manual", ...((g as any).customerId ? { customerId: (g as any).customerId } : {}) } as any);
                reloadGuests();
                toast({ tone: "success", title: t("guests.profile.addedToEvent", "Registered"), subtitle: i18nT("guests.profile.profileNameEvTitle.icu", "{value0} → {value1}", { value0: profile.name, value1: ev.title }) });
                close();
              } catch (e) { toast({ tone: "danger", title: t("guests.profile.addToEventFailed", "Couldn't register the guest"), subtitle: errText(e) }); }
            }}
              style={{ display: "block", width: "100%", textAlign: "left" as const, padding: "10px 14px", margin: "6px 0", border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, fontSize: 13, fontWeight: 600, color: T.n1, cursor: "pointer", fontFamily: T.font }}>
              {ev.title}
            </button>
          ))}
        </ModalWrap>
      ) : null}
      {modal === "edit"       ? <EditProfileModal profile={profile} onSave={async (next) => {
        /* Saved on the record now (updateGuest, PATCH /v1/admin/guests/:id) — it only changed local state. */
        const [firstName, ...rest] = next.name.trim().split(/\s+/);
        try {
          await updateGuest(g.id, { firstName, lastName: rest.join(" "), email: next.email.trim(), phone: next.phone ?? "", organization: next.company ?? "" } as any);
          logUpsert("guest", profile as unknown as Record<string, unknown>, next as unknown as Record<string, unknown>, { id: g.id, name: next.name });
          setProfile(next); reloadGuests();
          toast({ tone: "success", title: t("guests.profile.profileUpdated", "Profile updated") });
          return true;
        } catch (e) { toast({ tone: "danger", title: t("guests.profile.updateFailed", "Couldn't update the guest"), subtitle: errText(e) }); return false; }
      }} onClose={close} /> : null}
      {modal === "addSegment" ? <AddSegmentModal guestId={g.id} current={segments} onAdded={(s) => { setSegments((prev) => [...prev, s]); reloadGuests(); }} onClose={close} /> : null}
      <BookAppointmentSheet open={bookFollowUp} onClose={() => setBookFollowUp(false)} prefill={{ name: profile.name, email: profile.email, phone: profile.phone }} origin="guests" />
    </Screen>
  );
}
